import { API_BASE_URL } from '../config/api';
import { CusTomTimelineRow } from '../components/timeline/mock.ts';
import { buildStitchSegments } from '../components/timeline/timelineUtils.ts';

/**
 * Asks the backend to stitch all video-backed scenes (and any standalone audio)
 * into a single video, bypassing the in-browser canvas recording entirely.
 * The backend mixes the audio in, so the returned blob contains the final
 * video+audio track ready for download / YouTube publishing.
 */
export interface StitchedVideoResult {
  blob: Blob;
  downloadUrl: string;
  outputFileName: string;
}

/** Hang guard for the stitch POST + blob download (server stalls observed). */
export const STITCH_TIMEOUT_MS = 10 * 60 * 1000;

/** Distinct error so the UI can message a hung stitch differently. */
export class StitchTimeoutError extends Error {
  constructor(message = 'Video stitching timed out. Please try again.') {
    super(message);
    this.name = 'StitchTimeoutError';
  }
}

/** Coarse phase of the stitch job, surfaced for honest progress UI. */
export type StitchStage = 'sending' | 'stitching' | 'downloading';

export interface StitchOptions {
  /** Caller signal (e.g. user Cancel or a superseding publish). */
  signal?: AbortSignal;
  /** Hang guard; defaults to STITCH_TIMEOUT_MS. */
  timeoutMs?: number;
  /** Phase reporter for staged progress text. */
  onStage?: (stage: StitchStage) => void;
}

/**
 * Controller of the latest stitch job. Starting a new stitch aborts the
 * previous one (single-flight): re-publishing can never stack duplicate
 * `stitchVideo` requests that stall the connection pool.
 */
let currentStitchController: AbortController | null = null;

/** Abort the in-flight stitch job, if any (user Cancel / superseded). */
export const abortStitch = (reason?: unknown): void => {
  currentStitchController?.abort(reason);
  currentStitchController = null;
};

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';

/** Interval between 409 ("stitch already running") re-polls. */
const STITCH_CONFLICT_RETRY_MS = 15 * 1000;
/** Upper bound on 409 retries; the overall timeout/cancel still applies. */
const STITCH_CONFLICT_MAX_RETRIES = 10;

/** Sleep that rejects (with the abort reason) if the job is cancelled/timed out. */
const sleepUnlessAborted = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    const id = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(id);
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });

export const getStitchedVideoBlob = async (
  data: CusTomTimelineRow[],
  videoId: string = '123',
  token: string | null,
  options?: StitchOptions
): Promise<StitchedVideoResult> => {
  // Single-flight: a new stitch supersedes any previous one.
  abortStitch(new StitchTimeoutError('Superseded by a newer publish.'));
  const controller = new AbortController();
  currentStitchController = controller;

  // Propagate a caller abort (Cancel button / unmount) into this job.
  const forwardAbort = () => controller.abort(options?.signal?.reason);
  if (options?.signal?.aborted) {
    controller.abort(options.signal.reason);
  } else {
    options?.signal?.addEventListener('abort', forwardAbort, { once: true });
  }

  let timedOut = false;
  const timeoutMs = options?.timeoutMs ?? STITCH_TIMEOUT_MS;
  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    controller.abort(new StitchTimeoutError());
  }, timeoutMs);

  try {
    if (!token) {
      throw new Error('Authentication is required to stitch the video');
    }

    const segments = buildStitchSegments(data);

    if (!segments.some((segment) => segment.sourceType === 'video')) {
      throw new Error('No video scenes found to stitch');
    }

    options?.onStage?.('sending');
    // A 409 means another stitch for this video is still running (retry
    // after a timeout, double-click). Wait its turn instead of failing: the
    // running job holds the server lock and clears it on completion.
    let response: Response | null = null;
    for (let attempt = 0; ; attempt++) {
      response = await fetch(`${API_BASE_URL}/video/stitchVideo`, {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId,
          segments,
        }),
        signal: controller.signal,
      });

      if (response.status !== 409) {
        break;
      }
      if (attempt >= STITCH_CONFLICT_MAX_RETRIES) {
        throw new Error(
          'A video stitch is still running after several minutes. Please try again later.'
        );
      }
      options?.onStage?.('stitching');
      await sleepUnlessAborted(STITCH_CONFLICT_RETRY_MS, controller.signal);
    }

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    options?.onStage?.('stitching');
    const responseData = await response.json();

    if (!responseData || !responseData.downloadUrl) {
      throw new Error('No download URL in the response');
    }

    // Download the video blob from Azure
    options?.onStage?.('downloading');
    const videoResponse = await fetch(responseData.downloadUrl, {
      signal: controller.signal,
    });

    if (!videoResponse.ok) {
      throw new Error(
        `Failed to download stitched video with status ${videoResponse.status}`
      );
    }

    const blob = await videoResponse.blob();

    return {
      blob,
      downloadUrl: responseData.downloadUrl,
      outputFileName: responseData.outputFileName,
    };
  } catch (error) {
    if (isAbortError(error)) {
      throw timedOut
        ? new StitchTimeoutError()
        : error instanceof Error
          ? error
          : new Error('Stitch was cancelled.');
    }
    console.error('Error getting stitched video blob:', error);
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    options?.signal?.removeEventListener('abort', forwardAbort);
    if (currentStitchController === controller) {
      currentStitchController = null;
    }
  }
};